export const LEGACY_DANGEROUS_WASTE_COLUMNS = [
    { id: 'category', display_name: 'Kategori' },
    { id: 'un_number', display_name: 'UN-nr.' },
    { id: 'constituent', display_name: 'Indhold' },
    { id: 'instruction', display_name: 'Deklaration' },
];

export function normalizeDangerousWasteColumns(columns) {
    if (!Array.isArray(columns)) return [];

    const seen = new Set();
    return columns
        .map((column) => {
            const displayName =
                typeof column?.display_name === 'string' ? column.display_name.trim() : '';
            const explicitId = typeof column?.id === 'string' ? column.id.trim() : '';
            // A name-only column (Setup's id-less columns) is identified by its name instead,
            // so it still resolves through the id-keyed lookups everywhere else in this module
            // rather than being dropped for lacking an id.
            return { id: explicitId || displayName, display_name: displayName };
        })
        .filter((column) => {
            if (!column.id || !column.display_name || seen.has(column.id)) return false;
            seen.add(column.id);
            return true;
        });
}

export function getDangerousWasteColumnsOrFallback(columns) {
    const normalized = normalizeDangerousWasteColumns(columns);
    return normalized.length > 0 ? normalized : LEGACY_DANGEROUS_WASTE_COLUMNS;
}

export function getDangerousWasteRecordEval(record) {
    return record?.eval && typeof record.eval === 'object' ? record.eval : {};
}

// Predates the waste receiver's own configurable columns: some evals still key their values
// directly by these fixed names instead of an eval-time columns list, a positional values
// list, or the receiver's own (different) column ids — so they never resolve against whatever
// columns the receiver happens to have configured now. Recognized independently of that, using
// the typical order DEFAULT_WASTE_RECEIVER_COLUMNS still scaffolds a new receiver with.
const LEGACY_EVAL_KEY_ORDER = ['constituent', 'category', 'un_number', 'hazard_classes', 'instruction'];
const LEGACY_EVAL_KEY_ALIASES = {
    hazard_classes: ['hazard_classes', 'hazard_class'],
};
const LEGACY_EVAL_KEY_DISPLAY_NAMES = {
    constituent: 'Indhold',
    category: 'Kategori',
    un_number: 'UN-nr.',
    hazard_classes: 'Fareklasse(r)',
    instruction: 'Deklaration',
};

// Returns whichever alias of `canonicalKey` is actually present on this eval (e.g. an eval
// may write the singular `hazard_class` instead of `hazard_classes`), or undefined.
function findLegacyEvalKey(evalData, canonicalKey) {
    const aliases = LEGACY_EVAL_KEY_ALIASES[canonicalKey] || [canonicalKey];
    return aliases.find((alias) => Object.prototype.hasOwnProperty.call(evalData, alias));
}

function isLegacyKeyedEval(evalData) {
    return LEGACY_EVAL_KEY_ORDER.some((key) => findLegacyEvalKey(evalData, key));
}

// A column per legacy key actually present on this eval, skipping whichever are missing —
// missing one of them is fine, it just does not get a column.
function getLegacyKeyedEvalColumns(evalData) {
    return LEGACY_EVAL_KEY_ORDER
        .map((canonicalKey) => {
            const actualKey = findLegacyEvalKey(evalData, canonicalKey);
            return actualKey
                ? { id: actualKey, display_name: LEGACY_EVAL_KEY_DISPLAY_NAMES[canonicalKey] }
                : null;
        })
        .filter(Boolean);
}

// Newer evals carry a positional `values` array alongside the `columns` list that was in
// effect when the record was evaluated, rather than keying each value by column id directly.
// Looking the id up in that eval-time `columns` list (not the caller's) keeps values matched
// correctly even after the waste receiver's columns are later reordered, renamed, or removed.
// Some evals carry `values` without a `columns` list at all — those line up positionally with
// whatever columns the caller is currently rendering, so `columnIndex` is used instead.
function getPositionalDangerousWasteEvalValue(evalData, columnId, columnIndex) {
    if (!Array.isArray(evalData?.values)) return undefined;

    if (Array.isArray(evalData?.columns)) {
        const index = evalData.columns.findIndex((column) => column?.id === columnId);
        return index === -1 ? undefined : evalData.values[index];
    }

    return Number.isInteger(columnIndex) && columnIndex >= 0
        ? evalData.values[columnIndex]
        : undefined;
}

function formatDangerousWasteFieldValue(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.filter((item) => item != null).join(', ');
    if (value?.toDate && typeof value.toDate === 'function') {
        return value.toDate().toLocaleString();
    }

    return JSON.stringify(value);
}

export function getDangerousWasteRecordValue(record, columnId, columnIndex) {
    if (!record || !columnId) return '';

    const evalData = getDangerousWasteRecordEval(record);
    const value =
        getPositionalDangerousWasteEvalValue(evalData, columnId, columnIndex) ??
        // LEGACY_EVAL_FORMAT: Older evals key each column's value directly on the eval map.
        evalData[columnId] ??
        record[columnId] ??
        '';

    return formatDangerousWasteFieldValue(value);
}

// A legacy-keyed eval has no receiver-column ids at all, so a table (one fixed header row
// shared by every record) can only place it by assuming the receiver still has its columns in
// the typical LEGACY_EVAL_KEY_ORDER order — best-effort, not validated the way the positional
// `values` list is above. `columnIndex` is the queried column's position among all of the
// receiver's columns.
function getLegacyKeyedEvalValueAtIndex(evalData, columnIndex) {
    const canonicalKey = LEGACY_EVAL_KEY_ORDER[columnIndex];
    if (!canonicalKey) return undefined;
    const actualKey = findLegacyEvalKey(evalData, canonicalKey);
    return actualKey ? evalData[actualKey] : undefined;
}

// For a table/list view rendering one fixed set of columns across many records: match the
// eval's positional `values` list purely by index against those columns, ignoring any
// eval-time `columns` list the record itself carries (unlike getDangerousWasteRecordValue,
// which trusts that list). The index only means something when the eval's value count
// matches the caller's current column count exactly — a mismatch (columns added/removed on
// the waste receiver since the record was evaluated) means position can no longer be
// trusted, so every cell for that row is left blank rather than risk a value under the
// wrong header.
export function getDangerousWasteRecordTableValue(record, columnId, columnIndex, columnCount) {
    if (!record || !columnId) return '';

    const evalData = getDangerousWasteRecordEval(record);
    if (Array.isArray(evalData?.values)) {
        if (evalData.values.length !== columnCount) return '';
        return formatDangerousWasteFieldValue(evalData.values[columnIndex]);
    }

    const value =
        // LEGACY_EVAL_FORMAT: Older evals key each column's value directly on the eval map.
        evalData[columnId] ??
        getLegacyKeyedEvalValueAtIndex(evalData, columnIndex) ??
        record[columnId] ??
        '';
    return formatDangerousWasteFieldValue(value);
}

// The LLM's own explanation of the eval it produced, written to `eval.reason`. It is not a
// column: it is absent from every receiver's column list and from LEGACY_EVAL_KEY_ORDER, so it
// never surfaces as a value cell — it is only ever read through here. Records evaluated before
// the reason existed simply have none, which callers render as nothing at all.
export function getDangerousWasteRecordEvalReason(record) {
    const evalData = getDangerousWasteRecordEval(record);
    return formatDangerousWasteFieldValue(evalData.reason);
}

// The ids this record carries its values under, or null when it names none at all — a
// positional eval places its values by index instead, and has no ids to match against.
function getRecordValueColumnIds(evalData) {
    const recordColumns = normalizeDangerousWasteColumns(evalData.columns);
    if (recordColumns.length > 0) return new Set(recordColumns.map((column) => column.id));

    if (!Array.isArray(evalData.values) && isLegacyKeyedEval(evalData)) {
        return new Set(getLegacyKeyedEvalColumns(evalData).map((column) => column.id));
    }

    return null;
}

// A receiver column and a record may spell the same column differently (a receiver's
// `hazard_classes` against the singular `hazard_class` some evals write), so a receiver column
// resolves to whichever of its aliases the record actually uses, or to nothing when the record
// carries no value for it at all.
function resolveRecordColumnId(recordColumnIds, columnId) {
    const aliases = LEGACY_EVAL_KEY_ALIASES[columnId] || [columnId];
    return aliases.find((alias) => recordColumnIds.has(alias));
}

// The waste receiver, not the record, decides which columns exist and what each is called: a
// record is shown through the receiver's configured columns, so a column renamed on the
// receiver shows its new name, and one removed from the receiver is gone from every record.
// The record only decides which of those columns it carries a value for, matched by column id
// against its own eval-time columns (or, for a legacy-keyed eval, its keys) — so a record
// evaluated against columns the receiver no longer has simply shows fewer of them.
// `visibleColumns` are the receiver's columns to render; `allColumns` is its full set, hidden
// columns included, which is what a positional eval's values line up against.
export function getWasteReceiverRecordFields(record, visibleColumns = [], allColumns = visibleColumns) {
    const columns = normalizeDangerousWasteColumns(visibleColumns);
    if (columns.length === 0) return [];

    const fullColumns = normalizeDangerousWasteColumns(allColumns);
    const columnIndexById = new Map(fullColumns.map((column, index) => [column.id, index]));
    const evalData = getDangerousWasteRecordEval(record);
    const recordColumnIds = getRecordValueColumnIds(evalData);

    // A positional eval's index only means something while the receiver still has the column
    // count the record was evaluated against. A mismatch (columns added or removed since)
    // would put values under the wrong headers, so show none of them rather than the wrong
    // ones — the same rule the record list applies.
    if (
        recordColumnIds === null
        && Array.isArray(evalData.values)
        && evalData.values.length !== fullColumns.length
    ) {
        return [];
    }

    return columns
        .map((column) => {
            const columnIndex = columnIndexById.get(column.id);
            if (recordColumnIds === null) {
                return { ...column, value: getDangerousWasteRecordValue(record, column.id, columnIndex) };
            }

            const recordColumnId = resolveRecordColumnId(recordColumnIds, column.id);
            if (!recordColumnId) return null;

            // Only the value is read under the record's own id for this column; the title
            // shown is always the receiver's configured display_name.
            return { ...column, value: getDangerousWasteRecordValue(record, recordColumnId, columnIndex) };
        })
        .filter(Boolean);
}
